import React from 'react';
import { 
  IconButton, 
  useTheme, 
  alpha, 
  Tooltip, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Box
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { useUserSettings } from '../../context/UserSettingsContext';

const ThemeToggle: React.FC = () => {
  const theme = useTheme();
  const { settings, updateSettings } = useUserSettings();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeChange = async (newTheme: 'light' | 'dark' | 'system') => {
    await updateSettings({ theme: newTheme });
    handleClose();
  };

  const getThemeIcon = () => {
    switch (settings.theme) {
      case 'light':
        return <LightModeIcon sx={{ fontSize: 20 }} />;
      case 'dark':
        return <DarkModeIcon sx={{ fontSize: 20 }} />;
      case 'system':
      default:
        return <SettingsBrightnessIcon sx={{ fontSize: 20 }} />;
    }
  };


  const themeOptions = [
    { value: 'light', icon: <LightModeIcon sx={{ fontSize: 20 }} />, label: 'Light' },
    { value: 'dark', icon: <DarkModeIcon sx={{ fontSize: 20 }} />, label: 'Dark' },
    { value: 'system', icon: <SettingsBrightnessIcon sx={{ fontSize: 20 }} />, label: 'System' },
  ];

  return (
    <>
      <Tooltip title="Change theme" placement="bottom">
        <motion.div
          whileHover={{ y: -1 }}
          whileTap={{ y: 0 }}
          transition={{ duration: 0.15, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <IconButton
            onClick={handleClick}
            size="medium"
            color="inherit"
            sx={{
              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.08),
              }
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={settings.theme}
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 90 }}
                transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
              >
                {getThemeIcon()}
              </motion.div>
            </AnimatePresence>
          </IconButton>
        </motion.div>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 140,
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? `0px 8px 32px ${alpha('#000000', 0.4)}, 0px 2px 8px ${alpha('#000000', 0.2)}`
                : `0px 8px 32px ${alpha('#000000', 0.12)}, 0px 2px 8px ${alpha('#000000', 0.08)}`,
            }
          }
        }}
      >
        {themeOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={settings.theme === option.value}
            onClick={() => handleThemeChange(option.value as 'light' | 'dark' | 'system')}
            sx={{
              borderRadius: 1.5,
              mx: 0.5,
              my: 0.25,
              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.divider, 0.2),
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                }
              }
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: 36,
              color: settings.theme === option.value ? theme.palette.primary.main : 'inherit'
            }}>
              {option.icon}
            </ListItemIcon>
            <ListItemText 
              primary={option.label}
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: settings.theme === option.value ? 500 : 400
              }}
            />
            {settings.theme === option.value && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  ml: 1
                }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ThemeToggle;
